/**
 * Floating Guide Button
 *
 * Persistent chat button that appears in the corner of the screen.
 * Opens the GuideChatModal when clicked.
 * Styled with the selected guide's accent colour.
 *
 * Australian English throughout.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Sparkles } from 'lucide-react';
import { useGuide } from '../../context/GuideContext';
import GuideChatModal from './GuideChatModal';

const FloatingGuideButton = () => {
  const { selectedGuide, hasCompletedSelection, guideLoading } = useGuide();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Don't show until guide selection is complete and loaded
  if (guideLoading || !hasCompletedSelection) {
    return null;
  }

  const guide = selectedGuide;

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-40">
        {/* Tooltip */}
        <AnimatePresence>
          {showTooltip && !isChatOpen && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap"
            >
              <div
                className="px-3 py-2 rounded-lg text-sm font-medium text-white shadow-lg"
                style={{ backgroundColor: guide.accentColor }}
              >
                Chat with {guide.name}
                <div
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 rotate-45 w-2 h-2"
                  style={{ backgroundColor: guide.accentColor }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Button */}
        <motion.button
          onClick={() => setIsChatOpen(true)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white focus:outline-none focus:ring-4 focus:ring-white/30"
          style={{ backgroundColor: guide.accentColor }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label={`Chat with ${guide.name}`}
        >
          {/* Avatar initial or icon */}
          <div className="relative">
            <motion.span
              className="text-xl font-bold"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {guide.name[0]}
            </motion.span>

            {/* Chat indicator dot */}
            <motion.div
              className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>

          {/* Subtle pulse ring */}
          <motion.div
            className="absolute inset-0 rounded-full border-2"
            style={{ borderColor: guide.accentColor }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
        </motion.button>
      </div>

      {/* Chat Modal */}
      <GuideChatModal
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />
    </>
  );
};

export default FloatingGuideButton;
